import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { answerParts } from '@d-orca/shared/ai-answer';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { AiMarkdown } from '@components/ui/AiMarkdown/AiMarkdown';
import { AiChart } from '@components/Pages/AiAgentPage/MessageList/AiChart';
import { AiGuideImage } from '@components/Pages/AiAgentPage/MessageList/AiGuideImage';
import { AiTableDrawing } from '@components/Pages/AiAgentPage/MessageList/AiTableDrawing';
import { answerPartKey } from '@components/Pages/AiAgentPage/MessageList/answerPartKey';
import { useTranscriptScroll } from '@components/Pages/AiAgentPage/MessageList/useTranscriptScroll';
import { AiAnswerTrail } from '@components/ui/AiAnswerTrail/AiAnswerTrail';
import { AiStatusLine } from '@components/ui/AiStatusLine/AiStatusLine';
import type { AiDrawing } from '@apis/aiAgent';
import { drawingsOf, type IChatStatus } from '@utils/aiChat';
import type { CardMessage } from './useAiChat';
import styles from './cardMessages.module.css';

interface ICardMessagesProps {
  messages: CardMessage[];
  isStreaming: boolean;
  /** The most recent `status` frame — the wait, in words, as on the full page. */
  status: IChatStatus | null;
  error: string | null;
  /** Used to build the deep link the rich-content nudge points at. */
  siteId: string;
  conversationId: string | null;
  onLinkClick: (url: string) => void;
  /** The site's zone, for the receipt's time. Absent until the working site resolves. */
  tz?: string;
}

/**
 * Slim transcript for the dashboard card. An answer reads in the order the full
 * page gives it — prose, and each table or chart where the answer placed it
 * (`@d-orca/shared/ai-answer`) — with the full page's own components, but only
 * when the card is wide enough to hold a drawing; in a narrow card the drawings
 * are hidden and a nudge points to the full AI Agent page. Both are in the DOM
 * and the stylesheet's container query picks one, so a card resized on the
 * dashboard grid flips between them without a re-render.
 */
/** One drawing in the card, by kind — the same three the full page draws. */
function CardDrawing({ drawing }: { drawing: AiDrawing }) {
  switch (drawing.kind) {
    case 'table':
      return <AiTableDrawing table={drawing.table} />;
    case 'chart':
      return <AiChart chart={drawing.chart} />;
    case 'image':
      return <AiGuideImage image={drawing.image} />;
  }
}

export function CardMessages(props: ICardMessagesProps) {
  const { messages, isStreaming, status, error, siteId, conversationId, onLinkClick, tz } = props;
  const { t } = useTranslation();
  // The full page's rule: a question just sent goes to the top, and the answer
  // streaming under it never drags the view.
  // The list is its own scroll region, and the only thing that scrolls: the
  // dashboard around the card stays where the reader left it.
  const listRef = useRef<HTMLDivElement>(null);
  const { questionRef, questionAt } = useTranscriptScroll(messages, isStreaming, listRef);

  const fullViewHref = `/ai-agent/${encodeURIComponent(siteId)}${
    conversationId ? `?conversation=${encodeURIComponent(conversationId)}` : ''
  }`;

  return (
    <ScrollArea className={styles.list} viewportClassName={styles.listBody} viewportRef={listRef}>
      {messages.map((message, idx) => {
        const isAssistant = message.role === 'assistant';
        const isLast = idx === messages.length - 1;
        const isArriving = isStreaming && isLast;
        // `drawingsOf` also reads the singular `table`/`chart` a thread written
        // before `drawings` existed carries — without that, a restored
        // chart-only answer was a blank bubble with no way out to the full page.
        const parts = isAssistant ? answerParts(message.content, drawingsOf(message), !isArriving) : [];
        const hasDrawings = parts.some((part) => part.kind === 'drawing');

        // Nothing of the answer to show yet while streaming → typing dots,
        // and the full page's status line beside them. The live region mounts
        // with the dots, before the first `status` frame, so each sentence is
        // a change inside it and is announced (`MessageList` gives the why).
        if (isAssistant && parts.length === 0 && isArriving) {
          return (
            <div key={idx} className={`${styles.message} ${styles.assistant}`}>
              <div className={styles.waiting} role="status" aria-live="polite">
                <div className={styles.typing}>
                  <span />
                  <span />
                  <span />
                </div>
                <AiStatusLine status={status} />
              </div>
            </div>
          );
        }

        return (
          <div
            key={idx}
            ref={idx === questionAt ? questionRef : undefined}
            className={`${styles.message} ${isAssistant ? styles.assistant : styles.user}`}
          >
            <div className={`${styles.column} ${hasDrawings ? styles.columnWide : ''}`}>
              {isAssistant ? (
                // One bubble for the whole answer, as on the full page.
                parts.length > 0 && (
                  <div className={`${styles.bubble} ${styles.answer}`}>
                    {parts.map((part, n) =>
                      part.kind === 'text' ? (
                        // The tail still arriving is repaired for the render, as on the full page.
                        <AiMarkdown
                          key={answerPartKey(parts, n)}
                          content={part.text}
                          className={styles.markdown}
                          streaming={isArriving && n === parts.length - 1}
                        />
                      ) : (
                        <div key={answerPartKey(parts, n)} className={styles.drawings}>
                          <CardDrawing drawing={part.drawing} />
                        </div>
                      ),
                    )}
                  </div>
                )
              ) : (
                <div className={styles.bubble}>
                  <p className={styles.userText}>{message.content}</p>
                </div>
              )}
              {/* The reader's own stop — neutral, not a warning. */}
              {isAssistant && message.stopped && (
                <div className={styles.stopped}>{t('aiAgent.stoppedNotice')}</div>
              )}
              {/* The same links and receipt as the full page, once the answer is
                  whole. The next questions are above the card's input. */}
              {isAssistant && !isArriving && <AiAnswerTrail siteId={siteId} message={message} tz={tz} />}
              {hasDrawings && (
                <a
                  className={styles.nudge}
                  href={fullViewHref}
                  onClick={(e) => {
                    e.preventDefault();
                    onLinkClick(fullViewHref);
                  }}
                >
                  {t('dashboard.aiAgent.richContentNudge')}
                </a>
              )}
            </div>
          </div>
        );
      })}
      {error && <div className={styles.error}>{error}</div>}
    </ScrollArea>
  );
}
